import { Link } from 'react-router-dom';
import type { Project, Task } from '../lib/types';
import { STATUS_COLORS } from '../lib/types';
import {
  formatAbsoluteTime,
  formatDuration,
  formatRelativeTime,
  formatTokens,
  totalTokens,
} from '../lib/format';
import { cleanBranchName, gitHubCompareUrl } from '../lib/github';

interface Props {
  task: Task;
  projectSlug: string;
  project?: Project | null;
}

export default function TaskCard({ task, projectSlug, project }: Props) {
  // Duration: when a task is in-flight we measure from started_at→now;
  // when it's finished we use started→completed. Falls back to
  // queued→completed for legacy rows without a started_at.
  const isInFlight =
    task.status === 'running' ||
    task.status === 'queued' ||
    task.phase === 'planning' ||
    task.phase === 'executing';
  const duration = formatDuration(
    task.started_at ?? task.queued_at,
    isInFlight ? null : task.completed_at,
  );

  const displayBranch = task.branch ? cleanBranchName(task.branch) : '';
  const compareUrl =
    task.branch && project?.repo_url
      ? gitHubCompareUrl(project.repo_url, project.default_branch || 'main', task.branch)
      : null;

  return (
    <Link
      to={`/p/${projectSlug}/tasks/${task.id}`}
      className="block bg-white border border-slate-200 rounded p-2.5 hover:border-slate-400 transition"
    >
      <div className="text-sm font-medium text-slate-800 line-clamp-2 mb-1">
        {task.title}
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span
          className={`px-1.5 py-0.5 rounded border text-[10px] ${STATUS_COLORS[task.status]}`}
        >
          {task.status}
        </span>
        {totalTokens(task) > 0 && (
          <span className="font-mono">{formatTokens(totalTokens(task))} tok</span>
        )}
      </div>
      {displayBranch && (
        <div className="text-[10px] font-mono text-slate-400 mt-1 truncate">
          {compareUrl ? (
            <a
              href={compareUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sky-700 hover:text-sky-900 hover:underline"
              title="Open GitHub compare view in a new tab"
            >
              {displayBranch}
            </a>
          ) : (
            displayBranch
          )}
        </div>
      )}
      <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-1 truncate">
        <span title={formatAbsoluteTime(task.queued_at)}>
          {formatRelativeTime(task.queued_at)}
        </span>
        {duration && (
          <>
            <span>·</span>
            <span title={isInFlight ? 'Time since start (live)' : 'Total runtime'}>
              {isInFlight ? `running ${duration}` : `ran ${duration}`}
            </span>
          </>
        )}
      </div>
      {task.creator_name && (
        <div className="text-[10px] text-slate-400 mt-1 truncate">
          by {task.creator_name}
        </div>
      )}
    </Link>
  );
}
